import clsx from 'clsx';
import React, { useRef, useEffect, FC, ReactNode } from 'react';
import Icon from '../Icon';

interface DialogProps {
  isOpen: boolean;
  onClose?: () => void;
  children: ReactNode;
  headerContent?: ReactNode;
  hideHeader?: boolean;
  closeButtonAriaLabel: string;
  disableCloseOnOutsideClick?: boolean;
}

interface DialogHeaderProps {
  onClose?: () => void;
  headerContent?: ReactNode;
  closeButtonAriaLabel: string;
}
interface DialogContentProps {
  children: ReactNode;
  removePaddingTop?: boolean;
}

const Dialog = ({
  isOpen,
  onClose,
  children,
  headerContent,
  hideHeader,
  closeButtonAriaLabel,
  disableCloseOnOutsideClick,
}: DialogProps) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog === null) return;

    const focusableElements = dialog.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusableElement = focusableElements[0] as HTMLElement;
    const lastFocusableElement = focusableElements[
      focusableElements.length - 1
    ] as HTMLElement;

    if (isOpen) {
      dialog.showModal();
      document.body.classList.add('overflow-hidden');
    } else {
      dialog.close();
      document.body.classList.remove('overflow-hidden');
    }

    const lockFocusToModal = (event: KeyboardEvent) => {
      if (firstFocusableElement === null || lastFocusableElement === null)
        return;
      if (event.key === 'Tab') {
        if (event.shiftKey) {
          if (document.activeElement === firstFocusableElement) {
            lastFocusableElement.focus();
            event.preventDefault();
          }
        } else {
          if (document.activeElement === lastFocusableElement) {
            firstFocusableElement.focus();
            event.preventDefault();
          }
        }
      }
    };

    const handleEscKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (disableCloseOnOutsideClick) {
          event.preventDefault();
          return;
        }
        onClose?.();
      }
    };

    const handleOutsideClick = (event: MouseEvent) => {
      if (disableCloseOnOutsideClick) return;
      if (
        contentRef.current &&
        !contentRef.current.contains(event.target as Node)
      ) {
        onClose?.();
      }
    };

    dialog.addEventListener('click', handleOutsideClick);
    dialog.addEventListener('keydown', lockFocusToModal);
    dialog.addEventListener('keydown', handleEscKeyPress);

    return () => {
      dialog.removeEventListener('click', handleOutsideClick);
      dialog.removeEventListener('keydown', lockFocusToModal);
      dialog.removeEventListener('keydown', handleEscKeyPress);
    };
  }, [disableCloseOnOutsideClick, isOpen, onClose]);

  return (
    <dialog
      className="rounded-lg backdrop:bg-black backdrop:bg-opacity-50"
      ref={dialogRef}
      onClose={onClose}
    >
      <div ref={contentRef}>
        {!hideHeader && (
          <DialogHeader
            headerContent={headerContent}
            onClose={onClose}
            closeButtonAriaLabel={closeButtonAriaLabel}
          ></DialogHeader>
        )}
        <DialogContent removePaddingTop={!hideHeader}>
          {children}
        </DialogContent>
      </div>
    </dialog>
  );
};

const DialogContent: FC<DialogContentProps> = ({
  children,
  removePaddingTop,
}) => {
  return (
    <div className={clsx('p-4', removePaddingTop && 'pt-0')}>
      {children}
    </div>
  );
};
DialogContent.displayName = 'DialogContent';
const DialogHeader: FC<DialogHeaderProps> = ({
  headerContent,
  onClose,
  closeButtonAriaLabel,
}) => {
  return (
    <div
      className={clsx(
        'flex pb-0  pl-4',
        headerContent
          ? 'items-center justify-between gap-4'
          : 'justify-end'
      )}
    >
      {headerContent}
      <button
        aria-label={closeButtonAriaLabel}
        className="p-4 text-md"
        onClick={onClose}
      >
        <Icon name="close" className="h-6 w-6" />
      </button>
    </div>
  );
};
DialogHeader.displayName = 'DialogHeader';

export default Dialog;
